import { useMemo } from "react"
import type { FieldValues, Path, UseFormSetValue, UseFormWatch } from "react-hook-form"

import { InventoryHoldArea, SHELVING_DEFAULT_FORM_VALUES } from "../constants"
import { findFirstAvailableShelving, getHoldAreaOptions } from "../utils"

import { useGetInventoryShelvingOptions } from "./useGetInventoryShelvingOptions"

import type { WarehouseArea, WarehouseShelvingBinSize } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface UseInventoryDestinationFieldsParams<TFieldValues extends FieldValues> {
  name?: Path<TFieldValues>
  watch: UseFormWatch<TFieldValues>
  setValue: UseFormSetValue<TFieldValues>
  warehouseArea?: WarehouseArea | null
  selectedShelvingIds?: string[]
  autoSelectFirstAvailable?: boolean
}

interface InventoryDestinationFieldPaths<TFieldValues> {
  holdArea: Path<TFieldValues>
  shelving: Path<TFieldValues>
  binSize: Path<TFieldValues>
  aisleCode: Path<TFieldValues>
  sectionCode: Path<TFieldValues>
  binNumber: Path<TFieldValues>
  warehouseShelvingId: Path<TFieldValues>
}

interface UseInventoryDestinationFieldsReturn<TFieldValues extends FieldValues> {
  fieldPaths: InventoryDestinationFieldPaths<TFieldValues>
  holdArea: InventoryHoldArea | null
  binSize: WarehouseShelvingBinSize | null
  aisleCode: string | null
  sectionCode: string | null
  shouldShowShelvingFields: boolean
  holdAreaOptions: Option<InventoryHoldArea>[]
  aisleOptions: Option<string>[]
  shelfOptions: Option<string>[]
  binOptions: Option<number>[]
  isFetching: boolean
  isAisleFull: boolean
  isSectionFull: boolean
  handleHoldAreaChange: () => void
  handleBinSizeChange: () => void
  handleAisleCodeChange: (value: string | null) => void
  handleSectionCodeChange: (value: string | null) => void
  handleBinNumberChange: (value: number | null) => void
}

export const useInventoryDestinationFields = <TFieldValues extends FieldValues>({
  name,
  watch,
  setValue,
  warehouseArea,
  selectedShelvingIds,
  autoSelectFirstAvailable = true,
}: UseInventoryDestinationFieldsParams<TFieldValues>): UseInventoryDestinationFieldsReturn<TFieldValues> => {
  const baseFieldPath = name ? `${name}.` : ""

  const fieldPaths: InventoryDestinationFieldPaths<TFieldValues> = {
    holdArea: `${baseFieldPath}holdArea` as Path<TFieldValues>,
    shelving: `${baseFieldPath}shelving` as Path<TFieldValues>,
    binSize: `${baseFieldPath}shelving.binSize` as Path<TFieldValues>,
    aisleCode: `${baseFieldPath}shelving.aisleCode` as Path<TFieldValues>,
    sectionCode: `${baseFieldPath}shelving.sectionCode` as Path<TFieldValues>,
    binNumber: `${baseFieldPath}shelving.binNumber` as Path<TFieldValues>,
    warehouseShelvingId: `${baseFieldPath}shelving.warehouseShelvingId` as Path<TFieldValues>,
  }

  const [holdArea, binSize, aisleCode, sectionCode, warehouseShelvingId] = watch([
    fieldPaths.holdArea,
    fieldPaths.binSize,
    fieldPaths.aisleCode,
    fieldPaths.sectionCode,
    fieldPaths.warehouseShelvingId,
  ]) as [InventoryHoldArea | null, WarehouseShelvingBinSize | null, string | null, string | null, string | null]

  const shouldShowShelvingFields = holdArea === InventoryHoldArea.Warehouse

  const holdAreaOptions = useMemo(() => getHoldAreaOptions(warehouseArea), [warehouseArea])

  const {
    aisleOptions,
    shelfOptions,
    binOptions,
    shelvings,
    availableShelvings,
    isFetching,
    isAisleFull,
    isSectionFull,
  } = useGetInventoryShelvingOptions({
    warehouseAreaId: warehouseArea?.warehouseAreaId,
    binSize,
    aisleCode,
    sectionCode,
    warehouseShelvingId,
    selectedShelvingIds,
  })

  const handleHoldAreaChange = () => {
    setValue(fieldPaths.shelving, SHELVING_DEFAULT_FORM_VALUES as never, { shouldValidate: true })
  }

  const handleBinSizeChange = () => {
    setValue(fieldPaths.aisleCode, null as never, { shouldValidate: true })
    setValue(fieldPaths.sectionCode, null as never)
    setValue(fieldPaths.binNumber, null as never)
    setValue(fieldPaths.warehouseShelvingId, null as never)
  }

  const handleAisleCodeChange = (value: string | null) => {
    const availableShelving =
      autoSelectFirstAvailable && value ? findFirstAvailableShelving(availableShelvings, value) : null

    setValue(fieldPaths.sectionCode, (availableShelving?.sectionCode ?? null) as never, { shouldValidate: true })
    setValue(fieldPaths.binNumber, (availableShelving?.binNumber ?? null) as never, {
      shouldValidate: !!availableShelving,
    })
    setValue(fieldPaths.warehouseShelvingId, (availableShelving?.warehouseShelvingId ?? null) as never, {
      shouldValidate: !!availableShelving,
    })
  }

  const handleSectionCodeChange = (value: string | null) => {
    const availableShelving =
      autoSelectFirstAvailable && aisleCode && value
        ? findFirstAvailableShelving(availableShelvings, aisleCode, value)
        : null

    setValue(fieldPaths.binNumber, (availableShelving?.binNumber ?? null) as never, { shouldValidate: true })
    setValue(fieldPaths.warehouseShelvingId, (availableShelving?.warehouseShelvingId ?? null) as never, {
      shouldValidate: !!availableShelving,
    })
  }

  const handleBinNumberChange = (value: number | null) => {
    const selectedShelving = value
      ? shelvings.find(
          (shelving) =>
            shelving.aisleCode === aisleCode && shelving.sectionCode === sectionCode && shelving.binNumber === value
        )
      : null

    setValue(fieldPaths.warehouseShelvingId, (selectedShelving?.warehouseShelvingId ?? null) as never, {
      shouldValidate: true,
    })
  }

  return {
    fieldPaths,
    holdArea,
    binSize,
    aisleCode,
    sectionCode,
    shouldShowShelvingFields,
    holdAreaOptions,
    aisleOptions,
    shelfOptions,
    binOptions,
    isFetching,
    isAisleFull,
    isSectionFull,
    handleHoldAreaChange,
    handleBinSizeChange,
    handleAisleCodeChange,
    handleSectionCodeChange,
    handleBinNumberChange,
  }
}
